/**
 * Butler releases this app knows about, oldest first. The Butler sends its
 * version in `hello` (ME-OC-Butler src/usr/lib/butler/manifest.lua); when a
 * change there bumps it, add the same version here so the dashboard can say
 * how far behind a connected computer is.
 */
export const BUTLER_RELEASES: readonly { version: string; notes: string }[] = [
  { version: "0.1.0", notes: "Every Butler installed before versions were tracked." },
  { version: "0.2.0", notes: "butler-update / update.lua: verified in-place updates." },
  { version: "0.2.1", notes: "butler selftest: checks in game what the Butler assumes from the mod source." },
  { version: "0.3.0", notes: "Reads patterns through an ME Interface Terminal (Patterns page) and stock.read." },
  { version: "0.3.1", notes: "butler selftest checks the ME Interface Terminal; clearer error when it is missing." },
  { version: "0.4.0", notes: "network.fetch: lists everything stored, so the home page can monitor resources that have no rule." },
  { version: "0.4.1", notes: "AE change events: re-subscribes when AE drops the listener, polls stock while no events arrive." },
  { version: "0.4.2", notes: "butler events: prints every signal to debug AE change events; subscription state in the log." },
  { version: "0.4.3", notes: "AE change events accepted from any sender and used as re-read hints, so duplicated events no longer count twice." },
  { version: "0.4.4", notes: "Network reads are streamed one page at a time, so a big network no longer runs the computer out of memory." },
  { version: "0.4.5", notes: "Offline stocking keeps crafting a rule until it reaches target, not just back above min." },
  { version: "0.4.6", notes: "Offline stocking retries a refused craft soon at half the amount instead of waiting minutes." },
  { version: "0.5.0", notes: "Power control: reads the LSC, Deep Earth Heating Pumps and turbines, switches them by the Power page's rules, and relays far machines with butler-node." },
  { version: "0.5.1", notes: "Power: reads GT Super and Quantum Tanks, and a guard can watch one of them instead of AE." },
  { version: "0.5.2", notes: "storage.fetch: reads how full the storage cells are through transposers against the ME Drives (Storage page)." },
];

export const LATEST_BUTLER_VERSION = BUTLER_RELEASES[BUTLER_RELEASES.length - 1].version;

export interface ButlerVersionStatus {
  state: "current" | "outdated" | "newer" | "unknown";
  current: string | null;
  latest: string;
  /** Releases newer than the running one (0 unless outdated). */
  behind: number;
  /** Notes of the releases it is missing, oldest first. */
  missing: string[];
}

function parse(v: string): number[] | null {
  const m = /^v?(\d+)\.(\d+)\.(\d+)/.exec(v.trim());
  return m ? [Number(m[1]), Number(m[2]), Number(m[3])] : null;
}

/** Negative when a < b, 0 when equal, positive when a > b. */
export function compareVersions(a: string, b: string): number {
  const pa = parse(a);
  const pb = parse(b);
  if (!pa || !pb) return 0;
  for (let i = 0; i < 3; i++) if (pa[i] !== pb[i]) return pa[i] - pb[i];
  return 0;
}

export function butlerVersionStatus(version: string | null): ButlerVersionStatus {
  const latest = LATEST_BUTLER_VERSION;
  if (!version || !parse(version)) return { state: "unknown", current: version, latest, behind: 0, missing: [] };
  const newer = BUTLER_RELEASES.filter((r) => compareVersions(r.version, version) > 0);
  if (newer.length > 0) {
    return { state: "outdated", current: version, latest, behind: newer.length, missing: newer.map((r) => `${r.version}: ${r.notes}`) };
  }
  return { state: compareVersions(version, latest) > 0 ? "newer" : "current", current: version, latest, behind: 0, missing: [] };
}

/** One line for the dashboard and the log. */
export function describeButlerVersion(s: ButlerVersionStatus): string {
  switch (s.state) {
    case "current":
      return `v${s.current} (up to date)`;
    case "outdated":
      return `v${s.current}, ${s.behind} release${s.behind === 1 ? "" : "s"} behind v${s.latest}`;
    case "newer":
      return `v${s.current} (newer than this app knows, v${s.latest}; update the app)`;
    default:
      return `version ${s.current ?? "not reported"} (latest is v${s.latest})`;
  }
}
